import { z } from "zod";
import { Tag } from "./tag.js";
import { slugString } from "./slug.js";

// LogSet — atomic event in the new model. Replaces the nested
// WorkoutSet[] inside WorkoutExerciseEntry. Each set is its own DDB row
// keyed at pk=USER#<sub>, sk=SET#<performedAtMs>#<id>, with a sparse
// gsi1 (USER#<sub>#EX#<slug>, SET#<performedAtMs>#<id>) for per-exercise
// queries.
//
// Why flat-optional payload instead of a discriminated union per `kind`:
// Q1 of the design session — DDB document attribute layout fights Zod
// discriminators on PUT/UPDATE; flat-optional with a server-side
// invariant check (kind=weighted ⇒ reps + weight present) keeps the
// marshalling code simple and lets index queries project a stable set
// of attributes. The validation cost is centralised in the API route.

// Reuse the constraints from the existing WorkoutSet primitives so the
// importer's per-row conversion needs no extra coercion. Caps + units
// mirror packages/shared/src/schemas/workout.ts verbatim.
const Reps = z.number().int().min(0).max(999);
const WeightKg = z.number().min(0).max(2000);
const ExtraKg = z.number().min(-500).max(2000);
const P1RMPct = z.number().min(0).max(200);
const RPE = z.number().min(1).max(10);
const DurationSec = z.number().int().min(0).max(86400);
// 200 km ceiling — mirrors workout.ts SetDistanceM. Long outdoor rides
// pass 10 km; the old 10 km cap rejected them (inbox b44abfd0).
const DistanceM = z.number().min(0).max(200000);
const Side = z.enum(["left", "right", "both"]);

// Intra-set chain. Same shape as workout.ts's Dropset — duplicated
// rather than imported because the two schemas describe the same
// domain object and decoupling them lets PR 4's facade convert between
// shapes without circular dependencies. The two definitions are
// load-bearing-identical; if one changes, the other must too.
const DropsetVariant = z.enum(["classical", "myorep"]);
const DropsetStep = z.object({
  reps: Reps,
  weight: WeightKg.optional(),
  extra: ExtraKg.optional(),
  // Per-step completion stamp (Logger redesign slice 4). Mirrors
  // WorkoutSet.completedAt — present when the lifter marked the step
  // done in the Logger, absent while pending. Optional so legacy rows
  // without per-step state read as "treat-as-done on history render"
  // (no migration; the Logger short-circuits the missing stamp to ✓
  // for any saved dropset that predates this field).
  completedAt: z.iso.datetime().optional(),
});
export const Dropset = z.object({
  variant: DropsetVariant,
  steps: z.array(DropsetStep).min(1).max(8),
});
export type Dropset = z.infer<typeof Dropset>;

export const LogSetKind = z.enum([
  "weighted",
  "bodyweight",
  "p1rm",
  "loaded-carry",
  "cardio",
  // Isometric / time-based hold (plank, wall sit, bird-dog hold, the
  // physiotherapy isometrics — inbox 3dd20b1c). Logged by duration only,
  // reusing the same `duration` field cardio uses. No reps/weight.
  "timed",
]);
export type LogSetKind = z.infer<typeof LogSetKind>;

// ExerciseSlug constraint mirrors the catalog's slug rule — letters,
// digits, dashes; starts with a letter or digit; ≤64 chars.
const ExerciseSlug = slugString(64);

export const LogSet = z.object({
  id: z.uuid(),
  // userSub is the row's pk USER#<sub>. Stored on the document for
  // self-describing reads.
  userSub: z.string().min(1).max(128),
  exerciseSlug: ExerciseSlug,
  // Display snapshot. Same write-time-stamp pattern as the old
  // WorkoutExerciseEntry.exerciseName: server stamps from catalog at
  // POST time so historical rows keep displaying their original wording
  // even if the catalog row is later renamed.
  exerciseName: z.string().min(1).max(120).optional(),
  kind: LogSetKind,
  // Canonical clock for ordering. ISO-8601 UTC with ms precision; the
  // sk format is `SET#<performedAtMs>#<id>` (13-digit zero-padded epoch
  // ms). performedAt drives every read path (history, gsi1, alternation
  // detector) — this is the field that costs us if it's wrong.
  performedAt: z.iso.datetime(),
  // Optional explicit "completed" stamp. In v1 it equals performedAt by
  // default; the field stays distinct so a later distinction
  // (set-started-but-not-finished) is representable without a migration.
  completedAt: z.iso.datetime().optional(),

  // Payload — flat optional. Server validates kind/payload invariants:
  //   weighted     → reps + weight required
  //   bodyweight   → reps required, extra optional
  //   p1rm         → reps + p1rm required, rfl required
  //   loaded-carry → weight + distanceM required, reps optional
  //   cardio       → duration required, distance/weight/hr/calories optional
  //   timed        → duration required (seconds held), nothing else
  reps: Reps.optional(),
  weight: WeightKg.optional(),
  // Bilateral toggle: 1 = single-implement load, 2 = paired
  // implements moving together. Volume math reads this and
  // multiplies; rows without the field read as ×1 (setVolume.ts —
  // the "default to 2" an older comment claimed was the v2.29.0
  // double-count bug, never the contract). Optional for legacy rows;
  // the API stamps the catalog default when a weighted/bodyweight
  // payload omits it (IB-8 Slice 2, setFactory.buildLogSet). 1.45.0.
  loadedSides: z.union([z.literal(1), z.literal(2)]).optional(),
  extra: ExtraKg.optional(),
  p1rm: P1RMPct.optional(),
  rfl: z.boolean().optional(),
  distanceM: DistanceM.optional(),
  duration: DurationSec.optional(),
  // Cardio-only payload. avgHrBpm is the bout's mean heart rate (range
  // covers resting → max for a well-trained athlete); calories is a
  // best-effort estimate from the device. Both optional because
  // outdoor / off-device cardio rarely carries either.
  avgHrBpm: z.number().int().min(30).max(250).optional(),
  calories: z.number().min(0).max(5000).optional(),

  // Provenance of the weight value on this set. Data-only — no UI
  // surface renders this. Populated by resolveDefaultWeight at plan-
  // start ('prescribed-absolute' | 'computed-percent' | 'history') and
  // overwritten to 'manual' the moment the athlete edits the weight
  // cell. Optional so legacy rows without the field still read; absent
  // = 'manual' by convention (pre-Phase-1 rows were all athlete-typed
  // through the bare logger). Used by future post-session deviation
  // math + correct re-prefill on resume.
  weightSource: z
    .enum(["prescribed-absolute", "computed-percent", "history", "manual"])
    .optional(),

  // "This set went to the limit on the open variable" (measurement
  // contract §4b). Not a set *kind*: the athlete fixes one variable and
  // opens another, then reports what the open one turned out to be.
  // Strength: load fixed, reps open. Isometric hold: load fixed, time
  // open. Cardio: time or distance fixed, the other open. ONE flag for
  // all three — there must never be an `amtap`/`amdap` sibling; the next
  // slice extends this same field to timed/cardio rows. Today only
  // weighted/bodyweight sets can carry it (the logger chip's surface).
  //
  // What it changes: no math. Volume, e1RM and PR all read `reps` exactly
  // as before — same registry as `fail`. What it changes is how the
  // number READS: an AMRAP `reps` is a lower bound ("proved at least N"),
  // not a ceiling, so the analytics that later distinguish "hit the
  // prescription" from "measured a limit" have the one bit they need.
  //
  // Client-authored, like `fail`/`weightSource` and unlike the
  // server-derived `e1rmKg`: whether the athlete kept going until the
  // rep would not come is an observation only the athlete makes at the
  // bar. Nothing on the server can infer it from reps/weight, so the
  // server passes it through untouched and never stamps it.
  //
  // Optional + omit-the-default: persisted only as `true`, never as
  // `false`. Legacy rows without the field read as "not an AMRAP" — the
  // field is additive, no migration and no backfill.
  amrap: z.boolean().optional(),

  // Server-stamped estimated 1RM (Epley). Computed at POST time from
  // weight + reps using packages/shared/src/pr.ts:epley(). A failed set is
  // NOT excluded (AP-0p model): `reps` is the clean count, `fail` a pure
  // label, so a 3×165 that ended in a failed 4th keeps its e1RM point.
  // Null/undefined when kind != weighted, reps outside [1, 12], or
  // weight = 0. The client never authors this
  // field — the API route's batch handler overwrites whatever was sent.
  // Stored on the LogSet itself so the post-session summary can compute
  // session-level top e1RM without re-iterating the catalog or recomputing
  // formulas; legacy rows without e1rmKg fall back to read-time epley().
  e1rmKg: z.number().min(0).max(5000).optional(),

  // Shared optionals.
  rpe: RPE.optional(),
  // Reps in Reserve — coach-grade fatigue indicator. 0 = went to
  // failure (couldn't have done another rep); higher = more in the
  // tank. Domain 0-10 mirrors RPE's scale; in practice most logged
  // values land in 0-5. Distinct from RPE: RIR is a more objective
  // self-report than the perceived-exertion RPE scale, and the two
  // can coexist on a single set (RPE 9 + RIR 1 reads as "very hard,
  // one more in the tank"). Drives the Progress dashboard's Tier 2
  // fatigue-drift panel; absent on legacy rows so analyses degrade
  // gracefully ("no RIR data yet").
  rir: z.number().int().min(0).max(10).optional(),
  side: Side.optional(),
  // A pure label (AP-0p model, 2026-07-06): the set ended in a failed
  // attempt (always exactly one). `reps` already holds the clean reps
  // completed — the failed rep was decremented at log time — so `fail`
  // changes NO math (volume, e1RM, PR all use `reps`); it only records
  // "went to failure" for history + the timeline marker.
  fail: z.boolean().optional(),
  note: z.string().max(500).optional(),

  // Intra-set chain (still per-set, not cross-set; supersets are derived).
  dropset: Dropset.optional(),

  // Tags — namespaced, see schemas/tag.ts.
  tags: z.array(Tag).max(20).optional(),

  // Session linkage. A LogSet without sessionId is "drop-in tracking" —
  // legitimate but unusual; the logger UI always attaches one.
  sessionId: z.uuid().optional(),
  // Plan/program linkage. For multi-week programs the value follows the
  // composite slug `prog/<progId>/wk<n>/sess<m>/sid<sessionId>`: the trailing
  // `/sid<sessionId>` (MC-8) is the authoritative, reorder-safe link, while
  // the ordinal `sess<m>` stays as a fallback for rows written before MC-8.
  // For legacy single-day plans the format is `<planId>:<entryIdx>:<setIdx>`.
  fromPlanId: z.string().min(1).max(256).optional(),

  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type LogSet = z.infer<typeof LogSet>;

// Client → server payload. Server fills userSub/exerciseName/e1rmKg/
// createdAt/updatedAt. id optional but UUID when present (offline-first
// idempotency pattern, same as Workout/Session).
export const NewLogSet = LogSet.omit({
  id: true,
  userSub: true,
  exerciseName: true,
  e1rmKg: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.uuid().optional(),
});
export type NewLogSet = z.infer<typeof NewLogSet>;

// Update payload — all mutable fields optional, server merges. Slug,
// performedAt and sessionId are immutable after creation: changing them
// would resort the gsi1 sort key + invalidate the alternation detector
// over an already-saved session. The route enforces; here we leave them
// off the type so the client cannot author a PATCH body that asks for
// the impossible.
export const PatchLogSet = LogSet.omit({
  id: true,
  userSub: true,
  exerciseSlug: true,
  exerciseName: true,
  // e1rmKg is server-derived from reps/weight — the route re-stamps it
  // whenever those mutate, so the client never authors it.
  e1rmKg: true,
  performedAt: true,
  sessionId: true,
  createdAt: true,
  updatedAt: true,
}).partial();
export type PatchLogSet = z.infer<typeof PatchLogSet>;

// Validate kind/payload invariants. Returned issues are the canonical
// 400-response payload; the route maps them to invalid_payload. Pure
// function so the importer can call it offline and the test fixtures
// can use it standalone.
export const validateLogSetInvariants = (
  set: NewLogSet,
): { ok: true } | { ok: false; missing: string[]; reason: string } => {
  const missing: string[] = [];
  switch (set.kind) {
    case "weighted":
      if (set.reps === undefined) missing.push("reps");
      if (set.weight === undefined) missing.push("weight");
      break;
    case "bodyweight":
      if (set.reps === undefined) missing.push("reps");
      break;
    case "p1rm":
      if (set.reps === undefined) missing.push("reps");
      if (set.p1rm === undefined) missing.push("p1rm");
      if (set.rfl === undefined) missing.push("rfl");
      break;
    case "loaded-carry":
      if (set.weight === undefined) missing.push("weight");
      if (set.distanceM === undefined) missing.push("distanceM");
      break;
    case "cardio":
      // Cardio bouts log duration as the primary metric. distance,
      // weight (vest/sled load), avgHrBpm and calories ride
      // optionally — outdoor running without a watch is a valid log
      // with just `duration`.
      if (set.duration === undefined) missing.push("duration");
      else if (set.duration < 1) missing.push("duration");
      break;
    case "timed":
      // Isometric hold: seconds held is the only required metric.
      if (set.duration === undefined) missing.push("duration");
      else if (set.duration < 1) missing.push("duration");
      break;
  }
  if (missing.length > 0) {
    return {
      ok: false,
      missing,
      reason: `kind=${set.kind} requires ${missing.join(", ")}`,
    };
  }
  return { ok: true };
};
